import { useEffect, useMemo, useRef, useState } from "react";
import { ref, onValue, off } from "firebase/database";

// ─── Board config (original values) ───────────────────────────────────────
const SHOWS_PER_PAGE = 4;
const SHOWS_PAGE_MS = 8000; // park/page rotation interval
const TICK_MS = 60 * 1000; // hour/date refresh tick

const SHOW_PARK_ORDER = ["MK", "EPCOT", "HS", "AK", "DL", "CA"];

const SHOW_PARK_LABEL = {
  MK: "Magic Kingdom",
  EPCOT: "EPCOT",
  HS: "Hollywood Studios",
  AK: "Animal Kingdom",
  DL: "Disneyland",
  CA: "California Adventure",
};

const PARK_TZ_BY_KEY = {
  MK: { tz: "America/New_York", abbr: "ET" },
  EPCOT: { tz: "America/New_York", abbr: "ET" },
  HS: { tz: "America/New_York", abbr: "ET" },
  AK: { tz: "America/New_York", abbr: "ET" },
  DL: { tz: "America/Los_Angeles", abbr: "PT" },
  CA: { tz: "America/Los_Angeles", abbr: "PT" },
};

const DOT_FONT_FAMILY =
  '"LedCounter7", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
const DOT_TIME_COL = 9; // fixed width of the time segment
const DOT_NAME_COL = 39; // fixed width of the name segment
const DOT_ROLL_MS = 680;
const DOT_FPS = 30;

// ─── Pure helpers ──────────────────────────────────────────────────────────
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function padRight(str, n, ch = " ") {
  const s = String(str ?? "");
  if (s.length >= n) return s.slice(0, n);
  return s + ch.repeat(n - s.length);
}

function padLeft(str, n, ch = " ") {
  const s = String(str ?? "");
  if (s.length >= n) return s.slice(0, n);
  return ch.repeat(n - s.length) + s;
}

// Time segment: fixed-width, LEFT-justified (rendered flush left).
function fitTime(timeStr) {
  const tRaw = String(timeStr || "").replace(/\s+/g, " ").trim();
  return padRight(tRaw, DOT_TIME_COL, " ").slice(0, DOT_TIME_COL);
}

// Name segment: fixed-width, RIGHT-justified (rendered flush right).
function fitName(nameStr) {
  const nmRaw = String(nameStr || "").replace(/\s+/g, " ").trim();
  return padLeft(nmRaw, DOT_NAME_COL, " ").slice(0, DOT_NAME_COL);
}

function makeBlankName() {
  return " ".repeat(DOT_NAME_COL);
}

// If transitioning from a placeholder/blank board -> real data, snap (no roll)
// to avoid a static "gibberish" frame on the first real update.
function isPlaceholderBoard(rowsArr) {
  const arr = Array.isArray(rowsArr) ? rowsArr : [];
  if (!arr.length) return true;

  const allBlank = arr.every(
    (r) => !String(r?.time || "").trim() && !String(r?.name || "").trim()
  );
  if (allBlank) return true;

  const joined = arr.map((r) => String(r?.name || "")).join(" ");
  if (joined.includes("NO SCHEDULED ENTERTAINMENT") || joined.includes("THIS HOUR"))
    return true;

  return false;
}

function randomCharLike(targetChar) {
  const c = targetChar || " ";
  if (c === " ") return " ";
  if (/[0-9]/.test(c)) {
    const digs = "0123456789";
    return digs[Math.floor(Math.random() * digs.length)];
  }
  if (/[A-Z]/.test(c)) {
    const al = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    return al[Math.floor(Math.random() * al.length)];
  }
  if (/[a-z]/.test(c)) {
    const al = "abcdefghijklmnopqrstuvwxyz";
    return al[Math.floor(Math.random() * al.length)];
  }
  const misc = "-:./'()&";
  return misc[Math.floor(Math.random() * misc.length)];
}

// Roll-in frame for the NAME segment only (DOT_NAME_COL wide).
function buildRollFrame(fromStr, toStr, p01) {
  const L = DOT_NAME_COL;
  const lockCount = Math.floor(easeOutCubic(p01) * L);

  let out = "";
  for (let i = 0; i < L; i++) {
    if (i < lockCount) out += toStr[i] || " ";
    else {
      const pick = Math.random() < 0.25 ? fromStr[i] || " " : toStr[i] || " ";
      out += randomCharLike(pick);
    }
  }
  return out;
}

function normalizeShowName(name) {
  return String(name || "").replace(/\s+/g, " ").trim();
}

// Stable YYYY-MM-DD in a timezone, from a corrected-ms timestamp.
function getTzDateStrFor(dateObj, tz) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(dateObj);
  const y = parts.find((p) => p.type === "year")?.value || "1970";
  const m = parts.find((p) => p.type === "month")?.value || "01";
  const d = parts.find((p) => p.type === "day")?.value || "01";
  return `${y}-${m}-${d}`;
}

function getTzHourFor(dateObj, tz) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "2-digit",
    hour12: false,
  }).formatToParts(dateObj);
  const hh = parts.find((p) => p.type === "hour")?.value || "00";
  return Number(hh);
}

function formatTimeInTz(dateObjOrIso, tz) {
  const d = typeof dateObjOrIso === "string" ? new Date(dateObjOrIso) : dateObjOrIso;
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatHourRangeLabel(nowDateObj, tz, abbr) {
  const h = getTzHourFor(nowDateObj, tz);
  const start = h % 12 || 12;
  const end = (h + 1) % 12 || 12;
  const ampmStart = h < 12 ? "AM" : "PM";
  const h2 = (h + 1) % 24;
  const ampmEnd = h2 < 12 ? "AM" : "PM";
  return `${start}\u2013${end} ${ampmStart}${
    ampmEnd === ampmStart ? "" : `\u2192${ampmEnd}`
  } ${abbr}`;
}

function prevDay(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
}

// shows/byHour/{date} is a 24-element array (one slot per hour). This flattens
// every show across all hours for one park into a single list.
function flattenShowsFromBucket(bucketArr, parkKey) {
  if (!bucketArr) return [];
  const out = [];
  // Handles both an array (RTDB byHour) and an object keyed by hour.
  const slots = Array.isArray(bucketArr)
    ? bucketArr
    : typeof bucketArr === "object"
    ? Object.values(bucketArr)
    : [];
  for (const byHour of slots) {
    if (!byHour || typeof byHour !== "object") continue;
    const arr = Array.isArray(byHour?.[parkKey]) ? byHour[parkKey] : [];
    for (const x of arr) out.push(x);
  }
  return out;
}

// ─── DotMatrixBoard (original component, verbatim) ─────────────────────────
function DotMatrixBoard({ titleLeft, titleRight, rows }) {
  // rows: array of { time, name }. Only `name` animates (roll-in); `time`
  // renders static and flush-left so it never drifts during animation.
  const [displayNames, setDisplayNames] = useState(() =>
    (rows || []).map((r) => fitName(r?.name))
  );
  const prevTargetRef = useRef([]);
  const rafRef = useRef(0);
  const lastStartRef = useRef(0);
  const didInitRef = useRef(false);
  const hardEndRef = useRef(0);

  // Times are static — derived straight from props, no animation state.
  const timeSegs = Array.from({ length: SHOWS_PER_PAGE }).map((_, i) =>
    fitTime(rows?.[i]?.time)
  );

  useEffect(() => {
    // Target NAME segments (the only animated part).
    const targetRows = Array.from({ length: SHOWS_PER_PAGE }).map((_, i) => ({
      time: rows?.[i]?.time ?? "",
      name: rows?.[i]?.name ?? "",
    }));
    const target = targetRows.map((r) => fitName(r.name));

    const prevTarget = prevTargetRef.current;
    const prevDisplay = displayNames;

    const changed =
      prevTarget.length !== target.length ||
      target.some((t, i) => String(t) !== String(prevTarget[i] ?? ""));

    if (!changed) {
      if (displayNames.length !== target.length) setDisplayNames(target);
      return;
    }

    cancelAnimationFrame(rafRef.current);
    if (hardEndRef.current) clearTimeout(hardEndRef.current);

    const from = Array.from({ length: SHOWS_PER_PAGE }).map((_, i) =>
      String(prevDisplay[i] ?? prevTarget[i] ?? makeBlankName())
    );
    const to = target.map((x) => String(x ?? makeBlankName()));

    // If we were showing placeholder/blank rows, SNAP (no roll).
    const fromWasPlaceholder =
      isPlaceholderBoard(from.map((n) => ({ name: n }))) ||
      isPlaceholderBoard(prevTarget.map((n) => ({ name: n })));
    // Also SNAP when rolling INTO the placeholder: an interrupted roll
    // toward a never-changing target froze mid-roll gibberish on screen
    // for the whole empty hour.
    const toIsPlaceholder = isPlaceholderBoard(to.map((n) => ({ name: n })));
    if (fromWasPlaceholder || toIsPlaceholder) {
      didInitRef.current = true;
      prevTargetRef.current = to;
      setDisplayNames(to);
      return;
    }

    // First paint: show real text immediately (no roll-in gibberish).
    if (!didInitRef.current) {
      didInitRef.current = true;
      prevTargetRef.current = to;
      setDisplayNames(to);
      return;
    }

    prevTargetRef.current = to;
    lastStartRef.current = performance.now();
    hardEndRef.current = setTimeout(() => {
      setDisplayNames(to);
    }, DOT_ROLL_MS + 80);

    const frameMs = 1000 / DOT_FPS;

    const tick = (tNow) => {
      const start = lastStartRef.current;
      const p = clamp((tNow - start) / DOT_ROLL_MS, 0, 1);

      // throttle frames to ~DOT_FPS (Android WebView likes this better than
      // full-speed rAF for text-heavy updates)
      const last = tick._last || 0;
      if (tNow - last >= frameMs || p >= 1) {
        tick._last = tNow;
        const next = from.map((f, i) => buildRollFrame(f, to[i], p));
        setDisplayNames(next);
      }

      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else setDisplayNames(to);
    };
    tick._last = 0;

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (hardEndRef.current) clearTimeout(hardEndRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, titleLeft, titleRight]);

  useEffect(() => {
    if (!prevTargetRef.current.length && Array.isArray(rows) && rows.length) {
      const init = rows.map((r) => fitName(r?.name));
      prevTargetRef.current = init;
      setDisplayNames(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "10px 12px",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontFamily: DOT_FONT_FAMILY,
            fontSize: 22,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: "white",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {titleLeft}
        </div>
        <div
          style={{
            fontFamily: DOT_FONT_FAMILY,
            fontSize: 21,
            letterSpacing: 1.4,
            color: "white",
            whiteSpace: "nowrap",
            flex: "0 0 auto",
          }}
        >
          {titleRight}
        </div>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "12px 12px 12px",
          minHeight: 0,
        }}
      >
        {Array.from({ length: SHOWS_PER_PAGE }).map((_, i) => {
          const timeTxt = String(timeSegs[i] ?? fitTime(""));
          const nameTxt = String(displayNames[i] ?? makeBlankName());
          return (
            <div
              key={i}
              style={{
                position: "relative",
                height: 34,
                borderRadius: 10,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.10)",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.54) 100%)",
              }}
            >
              {/* dot grid (NO blur) */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage:
                    "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.14) 1px, transparent 1.6px)",
                  backgroundSize: "6px 6px",
                  opacity: 0.22,
                  pointerEvents: "none",
                }}
              />
              {/* subtle scanline */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage:
                    "repeating-linear-gradient(180deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 6px)",
                  opacity: 0.1,
                  pointerEvents: "none",
                }}
              />

              {/* Text row: time pinned left, name pinned right. space-between
                  guarantees each segment hugs its own edge — neither drifts
                  during the roll-in animation. */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0 8px 0 8px",
                  fontFamily: DOT_FONT_FAMILY,
                  fontSize: 20,
                  letterSpacing: 1.55,
                  color: "rgba(255,255,255,0.72)",
                  textShadow: "0 1px 0 rgba(0,0,0,0.55), 0 0 0 rgba(0,0,0,0)",
                  whiteSpace: "pre",
                  transform: "translateY(1px)",
                }}
                aria-label={(timeTxt + nameTxt).trim()}
              >
                {/* time — static, flush left */}
                <span style={{ flex: "0 0 auto" }}>{timeTxt}</span>
                {/* name — animated, flush right */}
                <span style={{ flex: "0 0 auto" }}>{nameTxt}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────
const ComingUpBoard = ({ database, subscribedParkKeys, style }) => {
  // Inject the LED @font-face once (file lives at /public/led_counter-7.ttf).
  useEffect(() => {
    const id = "led-counter-font-face";
    if (document.getElementById(id)) return;
    const styleEl = document.createElement("style");
    styleEl.id = id;
    styleEl.textContent =
      "@font-face{font-family:'LedCounter7';" +
      "src:url('/led_counter-7.ttf') format('truetype');" +
      "font-weight:400;font-style:normal;font-display:swap;}";
    document.head.appendChild(styleEl);
  }, []);

  // ── Server time ──────────────────────────────────────────────────────
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [offsetReady, setOffsetReady] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!database) return;
    const offsetRef = ref(database, ".info/serverTimeOffset");
    const handler = onValue(offsetRef, (snap) => {
      const v = Number(snap.val() || 0);
      if (Number.isFinite(v)) {
        setServerOffsetMs(v);
        setOffsetReady(true);
      }
    });
    return () => {
      off(offsetRef);
      if (typeof handler === "function") handler();
    };
  }, [database]);

  // 60s tick — drives hour/date recomputation.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // ── ET / PT date + hour keys (server-corrected) ──────────────────────
  const timeKeys = useMemo(() => {
    const nowMs = Date.now() + serverOffsetMs;
    const now = new Date(nowMs);
    const etDateStr = getTzDateStrFor(now, "America/New_York");
    const ptDateStr = getTzDateStrFor(now, "America/Los_Angeles");
    return {
      etDateStr,
      etPrevDateStr: prevDay(etDateStr),
      etHour: getTzHourFor(now, "America/New_York"),
      ptDateStr,
      ptPrevDateStr: prevDay(ptDateStr),
      ptHour: getTzHourFor(now, "America/Los_Angeles"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverOffsetMs, tick]);

  // ── Subscribe to the 4 shows/byHour buckets ──────────────────────────
  const [showsETToday, setShowsETToday] = useState(null);
  const [showsETPrev, setShowsETPrev] = useState(null);
  const [showsPTToday, setShowsPTToday] = useState(null);
  const [showsPTPrev, setShowsPTPrev] = useState(null);

  // One subscription per date bucket. Each re-subscribes only when its date
  // string changes (i.e. at a midnight rollover).
  useEffect(() => {
    if (!database || !timeKeys.etDateStr) return;
    const r = ref(database, "shows/byHour/" + timeKeys.etDateStr);
    const handler = onValue(r, (snap) =>
      setShowsETToday(snap.exists() ? snap.val() : null)
    );
    return () => {
      off(r);
      if (typeof handler === "function") handler();
    };
  }, [database, timeKeys.etDateStr]);

  useEffect(() => {
    if (!database || !timeKeys.etPrevDateStr) return;
    const r = ref(database, "shows/byHour/" + timeKeys.etPrevDateStr);
    const handler = onValue(r, (snap) =>
      setShowsETPrev(snap.exists() ? snap.val() : null)
    );
    return () => {
      off(r);
      if (typeof handler === "function") handler();
    };
  }, [database, timeKeys.etPrevDateStr]);

  useEffect(() => {
    if (!database || !timeKeys.ptDateStr) return;
    const r = ref(database, "shows/byHour/" + timeKeys.ptDateStr);
    const handler = onValue(r, (snap) =>
      setShowsPTToday(snap.exists() ? snap.val() : null)
    );
    return () => {
      off(r);
      if (typeof handler === "function") handler();
    };
  }, [database, timeKeys.ptDateStr]);

  useEffect(() => {
    if (!database || !timeKeys.ptPrevDateStr) return;
    const r = ref(database, "shows/byHour/" + timeKeys.ptPrevDateStr);
    const handler = onValue(r, (snap) =>
      setShowsPTPrev(snap.exists() ? snap.val() : null)
    );
    return () => {
      off(r);
      if (typeof handler === "function") handler();
    };
  }, [database, timeKeys.ptPrevDateStr]);

  // ── Shows for the current hour, per park (original dual-bucket logic) ──
  const hourShowsByPark = useMemo(() => {
    const { etDateStr, etHour, ptDateStr, ptHour } = timeKeys;
    const out = {};

    for (const pk of SHOW_PARK_ORDER) {
      const isWest = pk === "DL" || pk === "CA";
      const localDateStr = isWest ? ptDateStr : etDateStr;
      const localHour = isWest ? ptHour : etHour;

      const candidates = [];
      const addArr = (arr) => {
        if (!Array.isArray(arr)) return;
        for (const x of arr) candidates.push(x);
      };

      if (isWest) {
        addArr(flattenShowsFromBucket(showsPTToday, pk));
        addArr(flattenShowsFromBucket(showsPTPrev, pk));
        addArr(flattenShowsFromBucket(showsETToday, pk));
        addArr(flattenShowsFromBucket(showsETPrev, pk));
      } else {
        addArr(flattenShowsFromBucket(showsETToday, pk));
        addArr(flattenShowsFromBucket(showsETPrev, pk));
        addArr(flattenShowsFromBucket(showsPTToday, pk));
        addArr(flattenShowsFromBucket(showsPTPrev, pk));
      }

      const info = PARK_TZ_BY_KEY[pk] || { tz: "America/New_York" };

      const filtered = candidates.filter((x) => {
        const st = x?.startTime ? new Date(x.startTime) : null;
        if (!st || isNaN(st.getTime())) return false;
        const stDate = getTzDateStrFor(st, info.tz);
        const stHour = getTzHourFor(st, info.tz);
        return stDate === localDateStr && stHour === localHour;
      });

      // Deduplicate (a show can appear in multiple buckets).
      const seen = new Set();
      const deduped = [];
      for (const x of filtered) {
        const start = x?.startTime ? new Date(x.startTime).toISOString() : "";
        const name = normalizeShowName(x?.name || "");
        const id = String(x?.entityId || x?.id || "");
        const key =
          (id ? `id:${id}` : `name:${name}`) + `|start:${start}` + `|park:${pk}`;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(x);
      }

      if (deduped.length) out[pk] = deduped;
    }

    return out;
  }, [timeKeys, showsETToday, showsETPrev, showsPTToday, showsPTPrev]);

  // ── Park rotation list — FILTERED to subscribed parks ────────────────
  // This is the integration addition: the board only rotates through parks
  // the device subscribes to. If subscribedParkKeys is null/undefined, all
  // parks rotate (same convention as ParkPills).
  const rotationParks = useMemo(() => {
    if (Array.isArray(subscribedParkKeys)) {
      return SHOW_PARK_ORDER.filter((pk) => subscribedParkKeys.includes(pk));
    }
    return SHOW_PARK_ORDER;
  }, [subscribedParkKeys]);

  // hourParks: among the subscribed parks, those that actually have shows.
  // If none have shows, still rotate through subscribed parks (so the
  // "NO SCHEDULED ENTERTAINMENT THIS HOUR" message shows).
  const hourParks = useMemo(() => {
    const present = rotationParks.filter(
      (pk) => (hourShowsByPark[pk] || []).length > 0
    );
    return present.length ? present : rotationParks;
  }, [rotationParks, hourShowsByPark]);

  // ── Park / page rotation (8s) ────────────────────────────────────────
  const [showParkIdx, setShowParkIdx] = useState(0);
  const [showPageIdx, setShowPageIdx] = useState(0);

  // Reset rotation when the hour changes.
  useEffect(() => {
    setShowParkIdx(0);
    setShowPageIdx(0);
  }, [timeKeys.etHour]);

  // Keep park index in bounds if the subscribed-park list shrinks.
  useEffect(() => {
    setShowParkIdx((k) => (hourParks.length ? Math.min(k, hourParks.length - 1) : 0));
  }, [hourParks.length]);

  const currentParkKey =
    hourParks[Math.min(showParkIdx, hourParks.length - 1)] ||
    rotationParks[0] ||
    "MK";

  const currentParkPageCount = useMemo(() => {
    const arr = Array.isArray(hourShowsByPark?.[currentParkKey])
      ? hourShowsByPark[currentParkKey]
      : [];
    return Math.max(1, Math.ceil(arr.length / SHOWS_PER_PAGE));
  }, [hourShowsByPark, currentParkKey]);

  useEffect(() => {
    setShowPageIdx((p) => Math.min(p, currentParkPageCount - 1));
  }, [currentParkPageCount]);

  useEffect(() => {
    if (!hourParks.length) return;
    const id = setInterval(() => {
      setShowPageIdx((p) => {
        const nextPage = p + 1;
        if (nextPage < currentParkPageCount) return nextPage;
        setShowParkIdx((k) => (k + 1) % hourParks.length);
        return 0;
      });
    }, SHOWS_PAGE_MS);
    return () => clearInterval(id);
  }, [hourParks, currentParkPageCount]);

  const showParkKey = currentParkKey;
  const showParkLabel = SHOW_PARK_LABEL[showParkKey] || showParkKey;

  // ── Shows for the currently-displayed park ───────────────────────────
  const hourShows = useMemo(() => {
    const arr = Array.isArray(hourShowsByPark?.[showParkKey])
      ? hourShowsByPark[showParkKey]
      : [];
    const info = PARK_TZ_BY_KEY[showParkKey] || { tz: "America/New_York" };

    return arr
      .filter(Boolean)
      .map((x) => ({
        park: x?.park || showParkKey,
        name: normalizeShowName(x?.name || ""),
        timeLabel: x?.startTime
          ? formatTimeInTz(x.startTime, info.tz)
          : String(x?.timeLabel || "").trim(),
        startTime: x?.startTime || null,
        endTime: x?.endTime || null,
        entityId: x?.entityId || null,
      }))
      .filter((x) => x.name && x.timeLabel)
      .sort((a, b) => (Date.parse(a.startTime) || 0) - (Date.parse(b.startTime) || 0));
  }, [hourShowsByPark, showParkKey]);

  const hourShowsPage = useMemo(() => {
    const start = showPageIdx * SHOWS_PER_PAGE;
    return hourShows.slice(start, start + SHOWS_PER_PAGE);
  }, [hourShows, showPageIdx]);

  // ── Board rows ({ time, name }) ──────────────────────────────────────
  const dotRows = useMemo(() => {
    if (!hourShows.length) {
      return [
        { time: "", name: "NO SCHEDULED ENTERTAINMENT" },
        { time: "", name: "THIS HOUR" },
        { time: "", name: "" },
        { time: "", name: "" },
      ].slice(0, SHOWS_PER_PAGE);
    }

    const page = hourShowsPage;
    return Array.from({ length: SHOWS_PER_PAGE }).map((_, i) => {
      const x = page[i];
      if (!x) return { time: "", name: "" };
      const time = String(x.timeLabel || "").replace(/\s+/g, " ").trim();
      const name = String(x.name || "").toUpperCase();
      return { time, name };
    });
  }, [hourShows.length, hourShowsPage]);

  const showsHourLabel = useMemo(() => {
    const info = PARK_TZ_BY_KEY[showParkKey] || { tz: "America/New_York", abbr: "ET" };
    const nowMs = Date.now() + serverOffsetMs;
    return formatHourRangeLabel(new Date(nowMs), info.tz, info.abbr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showParkKey, serverOffsetMs, tick]);

  const boardLeftTitle = `COMING UP  ${showsHourLabel}`;
  const boardRightTitle = showParkLabel.toUpperCase();

  const blankRows = [
    { time: "", name: "" },
    { time: "", name: "" },
    { time: "", name: "" },
    { time: "", name: "" },
  ];

  return (
    <div style={{ ...shellStyle, ...style }}>
      <div style={shellFrame} />
      {offsetReady ? (
        <DotMatrixBoard
          titleLeft={boardLeftTitle}
          titleRight={boardRightTitle}
          rows={dotRows}
        />
      ) : (
        <DotMatrixBoard titleLeft="COMING UP" titleRight="" rows={blankRows} />
      )}
    </div>
  );
};

// ─── Shell styles (original dotBoardShell) ─────────────────────────────────
const shellStyle = {
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: 22,
  border: "1px solid rgba(0,0,0,0.22)",
  background:
    "linear-gradient(180deg, rgba(18,20,22,0.98) 0%, rgba(9,10,11,0.98) 100%)",
  boxShadow:
    "0 3px 0 rgba(0,0,0,0.30), 0 22px 46px rgba(0,0,0,0.22), 0 10px 18px rgba(0,0,0,0.18), 0 2px 0 rgba(255,255,255,0.10) inset",
  overflow: "hidden",
  minHeight: 0,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
};

const shellFrame = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  background:
    "radial-gradient(900px 260px at 40% -10%, rgba(255,255,255,0.10), transparent 58%), radial-gradient(800px 260px at 120% 30%, rgba(255,255,255,0.06), transparent 55%)",
  opacity: 0.65,
};

export default ComingUpBoard;
